import { useEffect, useState } from 'react';
import { Megaphone } from 'lucide-react';
import type { Member, MemberPublic } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';
import { Sheet } from '@/components/shared/Sheet';

interface PlayBet {
  id: string;
  bettorId: string;
  side: 'over' | 'under';
  amount: number;
  status: 'matched' | 'won' | 'lost';
}

interface PlayNotice {
  id: string;
  memberId: string;
  course: string;
  line: number;
  when: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: number;
  bets: PlayBet[];
  actualScore?: number;
}

const STORAGE_KEY = 'bagtag:play-notices';

function loadNotices(): PlayNotice[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as PlayNotice[] : [];
  } catch {
    return [];
  }
}

function saveNotices(notices: PlayNotice[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notices));
}

interface Props {
  member: Member;
  members: MemberPublic[];
  onOpenProfile: (id: string) => void;
}

export function ActionView({ member, members, onOpenProfile }: Props) {
  const [notices, setNotices] = useState<PlayNotice[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ course: 'Arlington — White / Blue', line: '82', when: 'Saturday AM' });
  const [betNotice, setBetNotice] = useState<PlayNotice | null>(null);
  const [betSide, setBetSide] = useState<'over' | 'under'>('under');
  const [betAmount, setBetAmount] = useState('25');

  useEffect(() => {
    setNotices(loadNotices());
  }, []);

  const persist = (next: PlayNotice[]) => {
    setNotices(next);
    saveNotices(next);
  };

  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));

  const myActiveBets = notices.flatMap((pn) =>
    pn.bets.filter((b) => b.bettorId === member.id && b.status === 'matched').map((b) => ({ pn, b })),
  );
  const myWonLost = notices.flatMap((pn) =>
    pn.bets.filter((b) => b.bettorId === member.id && (b.status === 'won' || b.status === 'lost')).map((b) => ({ pn, b })),
  );
  const myMoneyAtRisk = myActiveBets.reduce((s, x) => s + x.b.amount, 0);
  const myNet = myWonLost.reduce((s, x) => s + (x.b.status === 'won' ? x.b.amount : -x.b.amount), 0);

  const createNotice = () => {
    const pn: PlayNotice = {
      id: `pn${Date.now()}`,
      memberId: member.id,
      course: draft.course,
      line: Number(draft.line),
      when: draft.when,
      status: 'open',
      createdAt: Date.now(),
      bets: [],
    };
    persist([pn, ...notices]);
    setShowCreate(false);
  };

  const placeBet = () => {
    if (!betNotice) return;
    const bet: PlayBet = {
      id: `b${Date.now()}`,
      bettorId: member.id,
      side: betSide,
      amount: Number(betAmount) || 25,
      status: 'matched',
    };
    persist(notices.map((pn) => (pn.id === betNotice.id ? { ...pn, bets: [...pn.bets, bet] } : pn)));
    setBetNotice(null);
  };

  const sorted = [...notices].sort((a, b) => {
    const order = { open: 0, in_progress: 1, completed: 2, cancelled: 3 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return b.createdAt - a.createdAt;
  });

  return (
    <div className="space-y-3 pb-5">
      <p className="text-xs leading-[1.5] text-ink-soft">
        Members post their upcoming rounds with an over/under line. Bet on whether they beat it.
      </p>

      {(myActiveBets.length > 0 || myWonLost.length > 0) && (
        <div className="grid grid-cols-3 gap-2.5 rounded-[14px] bg-paper p-3.5 shadow-bt">
          <div className="text-center">
            <div className="text-[10px] font-bold tracking-wide text-mute">AT RISK</div>
            <div className="mt-1 font-serif text-xl font-bold text-gold">${myMoneyAtRisk}</div>
            <div className="text-[9px] text-mute">{myActiveBets.length} active</div>
          </div>
          <div className="border-x border-line text-center">
            <div className="text-[10px] font-bold tracking-wide text-mute">NET</div>
            <div className={`mt-1 font-serif text-xl font-bold ${myNet > 0 ? 'text-good' : myNet < 0 ? 'text-bad' : 'text-ink'}`}>
              {myNet >= 0 ? '+' : ''}${myNet}
            </div>
            <div className="text-[9px] text-mute">{myWonLost.length} settled</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-bold tracking-wide text-mute">RECORD</div>
            <div className="mt-1 font-serif text-xl font-bold text-navy">
              {myWonLost.filter((x) => x.b.status === 'won').length}-{myWonLost.filter((x) => x.b.status === 'lost').length}
            </div>
            <div className="text-[9px] text-mute">W-L</div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowCreate(true)}
        className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-navy py-3.5 text-sm font-bold tracking-wide text-paper shadow-bt-md"
      >
        <Megaphone size={18} className="text-gold" /> Post Your Upcoming Round
      </button>

      {sorted.filter((pn) => pn.status === 'open').length === 0 && (
        <div className="py-6 text-center text-[13px] text-mute">No live action right now. Be the first to post.</div>
      )}

      {sorted.filter((pn) => pn.status === 'open').map((pn) => {
        const who = pn.memberId === member.id ? member : memberById[pn.memberId];
        const isMine = pn.memberId === member.id;
        return (
          <div key={pn.id} className="overflow-hidden rounded-2xl border-2 border-gold bg-paper shadow-bt">
            <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
              {who ? <Avatar member={who} size={42} /> : <div className="h-[42px] w-[42px] rounded-full bg-line" />}
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => onOpenProfile(pn.memberId)} className="font-serif text-[15px] font-bold text-ink">
                  {who?.name || 'Member'}
                  {isMine && <span className="text-gold-deep"> · You</span>}
                </button>
                <div className="mt-0.5 text-[11px] text-mute">{pn.course} · {pn.when}</div>
              </div>
              <span className="rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold tracking-wide text-navy">LIVE</span>
            </div>
            <div className="px-4 py-3.5">
              <div className="mb-3 flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-bold tracking-wide text-mute">O/U LINE</div>
                  <div className="font-serif text-[30px] font-bold leading-none text-navy">{pn.line}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold tracking-wide text-mute">TEE TIME</div>
                  <div className="mt-1 font-serif text-base font-bold text-navy">{pn.when}</div>
                </div>
              </div>
              <div className="flex gap-2">
                {!isMine && (
                  <button type="button" onClick={() => setBetNotice(pn)} className="flex-1 rounded-[10px] bg-gold py-2.5 text-xs font-bold text-navy">
                    Place bet
                  </button>
                )}
                {isMine && (
                  <button
                    type="button"
                    onClick={() => persist(notices.map((x) => (x.id === pn.id ? { ...x, status: 'cancelled' as const } : x)))}
                    className="flex-1 rounded-[10px] border border-line bg-paper py-2.5 text-xs font-bold text-ink-soft"
                  >
                    Cancel notice
                  </button>
                )}
              </div>
              {pn.bets.length > 0 && (
                <div className="mt-2 text-[11px] text-mute">{pn.bets.length} bet{pn.bets.length === 1 ? '' : 's'} placed</div>
              )}
            </div>
          </div>
        );
      })}

      {showCreate && (
        <Sheet title="Post Upcoming Round" onClose={() => setShowCreate(false)}>
          <div className="space-y-3 px-4 pb-8 pt-2">
            <div>
              <div className="mb-1.5 text-[10px] font-bold tracking-wide text-mute">COURSE</div>
              <input value={draft.course} onChange={(e) => setDraft({ ...draft, course: e.target.value })} className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-sm outline-none" />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-bold tracking-wide text-mute">WHEN</div>
              <input value={draft.when} onChange={(e) => setDraft({ ...draft, when: e.target.value })} className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-sm outline-none" />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-bold tracking-wide text-mute">O/U LINE</div>
              <input type="number" value={draft.line} onChange={(e) => setDraft({ ...draft, line: e.target.value })} className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-center font-serif text-2xl font-bold outline-none" />
            </div>
            <button type="button" onClick={createNotice} className="w-full rounded-xl bg-navy py-3.5 text-sm font-bold text-paper">Post notice</button>
          </div>
        </Sheet>
      )}

      {betNotice && (
        <Sheet title="Place Bet" onClose={() => setBetNotice(null)}>
          <div className="space-y-3 px-4 pb-8 pt-2">
            <div className="text-center font-serif text-lg font-bold text-navy">Line: {betNotice.line}</div>
            <div className="flex gap-2">
              {(['under', 'over'] as const).map((s) => (
                <button key={s} type="button" onClick={() => setBetSide(s)} className={`flex-1 rounded-[10px] py-2.5 text-sm font-bold capitalize ${betSide === s ? 'bg-navy text-cream' : 'border border-line bg-paper text-ink-soft'}`}>{s}</button>
              ))}
            </div>
            <input type="number" value={betAmount} onChange={(e) => setBetAmount(e.target.value)} className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-center font-mono text-xl font-bold outline-none" />
            <button type="button" onClick={placeBet} className="w-full rounded-xl bg-gold py-3.5 text-sm font-bold text-navy">Confirm bet</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
